/**
 * Client half of the API validation-message contract.
 *
 * A validation response carries `errors: [{ severity, errormessage, fieldname }]`, where `severity` is
 * one of the `ERROR_SEVERITY` values below and `fieldname` is a real field name (or absent, for a rule
 * that spans several fields or comes from a stored procedure).
 *
 * Before this module, severity travelled in `fieldname` — the server literally sent
 * `fieldname: "WARNING"` — so callers filtered with `e.fieldname === "WARNING"` and then rendered
 * `fieldname + ": " + errormessage`, which is why warning dialogs used to open with the word
 * "WARNING: " stamped in front of every line. Both halves of that are fixed here: the filter reads
 * `severity`, and the prefix only appears when `fieldname` holds something that is actually a field.
 *
 * Three WM screens each had their own copy of the map/join; they all call `formatErrorList` now.
 */

/** Mirrors ErrorSeverity in Argos.Core.Data/DTO/Common/ErrorMessage.cs. */
export const ERROR_SEVERITY = {
    ERROR: 'ERROR',
    WARNING: 'WARNING',
};

/** Separator between messages. HTML, because every consumer renders into a DevExtreme dialog. */
const LINE_BREAK = '<br/>';

/**
 * Renders the messages of one severity as a single string for a dialog.
 *
 * @param {Array<{severity?: string, errormessage?: string, fieldname?: string}>|null|undefined} errors
 *        The `errors` array from the response. Missing or empty yields `''`.
 * @param {string} [severity] One of `ERROR_SEVERITY`. Omit to include every entry.
 * @returns {string} The joined messages, `''` when there is nothing to show.
 *
 * If the filter matches nothing but `errors` is non-empty, every entry is returned instead. That
 * fallback is deliberate: a server path that forgets to set `severity` would otherwise turn a dialog
 * the user needs to read into an empty one, which reads as "no problem" and is worse than showing a
 * message under a slightly wrong heading.
 */
export function formatErrorList(errors, severity) {
    if (!Array.isArray(errors) || errors.length === 0) return '';

    let matching = errors;
    if (severity) {
        matching = errors.filter(e => e && e.severity === severity);
        if (matching.length === 0) matching = errors;
    }

    return matching
        .filter(e => e && e.errormessage)
        .map(e => (e.fieldname ? `${e.fieldname}: ` : '') + e.errormessage)
        .join(LINE_BREAK);
}
